'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Lang = 'no' | 'en';

export interface Translations {
  // ── Navigation ──
  navHome: string;
  navMachines: string;
  navSubscription: string;
  navAbout: string;
  navContact: string;
  navLogin: string;
  navSignup: string;
  navDashboard: string;
  navInvoice: string;
  navSettings: string;
  navLogout: string;
  // ── Footer ──
  footerRights: string;
  footerLang: string;
  footerBlog: string;
  footerKarriere: string;
  footerPartnere: string;
  footerStatus: string;
  // ── Homepage Hero ──
  heroLabel: string;
  heroTagline: string;
  heroTitle: string;
  heroSubtitle: string;
  heroCta: string;
  heroCtaSecondary: string;
  heroScrollLabel: string;
  // ── Homepage Stats ──
  statMachines: string;
  statCountries: string;
  statYears: string;
  statUpdates: string;
  statMachinesLabel: string;
  statCountriesLabel: string;
  statYearsLabel: string;
  statUpdatesLabel: string;
  // ── Machine Sections ──
  machineSeriesLabel: string;
  machineHeavyLabel: string;
  machineTechLabel: string;
  machine1Name: string;
  machine1Desc: string;
  machine1Spec1Label: string;
  machine1Spec1Value: string;
  machine1Spec2Label: string;
  machine1Spec2Value: string;
  machine1Spec3Label: string;
  machine1Spec3Value: string;
  machine1Spec4Label: string;
  machine1Spec4Value: string;
  machine1Cta: string;
  machine2Name: string;
  machine2Desc: string;
  machine2Spec1Label: string;
  machine2Spec1Value: string;
  machine2Spec2Label: string;
  machine2Spec2Value: string;
  machine2Spec3Label: string;
  machine2Spec3Value: string;
  machine2Spec4Label: string;
  machine2Spec4Value: string;
  machine2Cta: string;
  machine3Name: string;
  machine3Desc: string;
  machine3Spec1Label: string;
  machine3Spec1Value: string;
  machine3Spec2Label: string;
  machine3Spec2Value: string;
  machine3Spec3Label: string;
  machine3Spec3Value: string;
  machine3Spec4Label: string;
  machine3Spec4Value: string;
  machine3Cta: string;
  // ── Software Updates Section ──
  updatesLabel: string;
  updatesTitle: string;
  updatesViewAll: string;
  updateStatusStable: string;
  updateStatusBeta: string;
  updateStatusMaintenance: string;
  updateStatusNew: string;
  // ── About Section ──
  aboutLabel: string;
  aboutTitle: string;
  aboutBody1: string;
  aboutBody2: string;
  aboutQuote: string;
  aboutCta: string;
  aboutContactCta: string;
  aboutStat1Label: string;
  aboutStat2Label: string;
  aboutStat3Label: string;
  aboutStat4Label: string;
  // ── Contact Section ──
  contactTitle: string;
  contactSubtitle: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactMessage: string;
  contactSend: string;
  contactSending: string;
  contactSuccess: string;
  contactNamePlaceholder: string;
  contactEmailPlaceholder: string;
  contactPhonePlaceholder: string;
  contactMessagePlaceholder: string;
  // ── Contact Section (extended) ──
  contactLabel: string;
  contactHeading: string;
  contactBody: string;
  contactEmailLabel: string;
  contactPhoneLabel: string;
  contactAddressLabel: string;
  contactHoursLabel: string;
  contactHoursValue: string;
  contactFormName: string;
  contactFormNamePlaceholder: string;
  contactFormEmail: string;
  contactFormEmailPlaceholder: string;
  contactFormCompany: string;
  contactFormCompanyPlaceholder: string;
  contactFormMessage: string;
  contactFormMessagePlaceholder: string;
  contactFormSubmit: string;
  contactToast: string;
  // ── Newsletter ──
  newsletterTitle: string;
  newsletterSubtitle: string;
  newsletterPlaceholder: string;
  newsletterBtn: string;
  newsletterSuccess: string;
  newsletterCompanyPlaceholder: string;
  newsletterOrgPlaceholder: string;
  newsletterAddressPlaceholder: string;
  newsletterEmailPlaceholder: string;
  // ── Newsletter (extended) ──
  newsletterLabel: string;
  newsletterHeading: string;
  newsletterBody: string;
  newsletterEmailLabel: string;
  newsletterEmailPlaceholder: string;
  newsletterPhoneLabel: string;
  newsletterPhonePlaceholder: string;
  newsletterEmailToggle: string;
  newsletterSmsToggle: string;
  newsletterSubmit: string;
  newsletterDisclaimer: string;
  newsletterToast: string;
  // ── Machines Page ──
  machinesPageTitle: string;
  machinesPageSubtitle: string;
  machinesCountLabel: string;
  machinesSearchPlaceholder: string;
  machinesFilterAll: string;
  machinesFilterIndustrial: string;
  machinesFilterHeavy: string;
  machinesFilterTech: string;
  machinesFilterCNC: string;
  machinesColName: string;
  machinesColCategory: string;
  machinesColStatus: string;
  machinesColVersion: string;
  machinesColAction: string;
  machinesLearnMore: string;
  machinesViewAll: string;
  machinesAddToCart: string;
  machinesPagination: string;
  machinesStatusActive: string;
  machinesStatusBeta: string;
  machinesStatusMaintenance: string;
  // ── Pricing Page ──
  pricingTitle: string;
  pricingSubtitle: string;
  pricingMonthly: string;
  pricingAnnual: string;
  pricingGetStarted: string;
  pricingContactSales: string;
  pricingMostPopular: string;
  pricingPlan1Name: string;
  pricingPlan1Price: string;
  pricingPlan1Desc: string;
  pricingPlan2Name: string;
  pricingPlan2Price: string;
  pricingPlan2Desc: string;
  pricingPlan3Name: string;
  pricingPlan3Price: string;
  pricingPlan3Desc: string;
  pricingPlan4Name: string;
  pricingPlan4Price: string;
  pricingPlan4Desc: string;
  pricingTrustBadge1: string;
  pricingTrustBadge2: string;
  pricingTrustBadge3: string;
  pricingPerMonth: string;
  // ── Auth Modal ──
  authLoginTitle: string;
  authSignupTitle: string;
  authEmailLabel: string;
  authPasswordLabel: string;
  authNameLabel: string;
  authCompanyLabel: string;
  authPhoneLabel: string;
  authLoginBtn: string;
  authSignupBtn: string;
  authForgotPassword: string;
  authNoAccount: string;
  authHaveAccount: string;
  authDividerText: string;
  authSocialBtn: string;
  authEmailOptIn: string;
  authSmsOptIn: string;
  authLoginSuccess: string;
  authSignupSuccess: string;
  authLoginError: string;
  authSignupError: string;
  authNamePlaceholder: string;
  authEmailPlaceholder: string;
  authPasswordPlaceholder: string;
  authCompanyPlaceholder: string;
  authPhonePlaceholder: string;
  // ── Invoice Modal ──
  invoiceTitle: string;
  invoiceCompanyLabel: string;
  invoiceOrgLabel: string;
  invoiceAddressLabel: string;
  invoiceEmailLabel: string;
  invoicePlanLabel: string;
  invoicePeriodLabel: string;
  invoiceAmountLabel: string;
  invoiceSubmitBtn: string;
  invoiceSuccess: string;
  invoiceCompanyPlaceholder: string;
  invoiceOrgPlaceholder: string;
  invoiceAddressPlaceholder: string;
  invoiceEmailPlaceholder: string;
  // ── Dashboard Sidebar ──
  dashOverview: string;
  dashMachines: string;
  dashUpdates: string;
  dashServicePlan: string;
  dashInvoices: string;
  dashSupport: string;
  dashPerformance: string;
  dashTeam: string;
  dashSaved: string;
  dashAlerts: string;
  dashAccount: string;
  dashHealth: string;
  dashMaintenance: string;
  dashContracts: string;
  dashAnalytics: string;
  dashShop: string;
  dashTraining: string;
  dashWarranty: string;
  dashEnergy: string;
  dashCustomAlerts: string;
  dash2FA: string;
  dashApi: string;
  dashFeedback: string;
  dashAnnouncements: string;
  dashDarkMode: string;
  // ── Dashboard Overview ──
  dashOverviewTitle: string;
  dashWelcome: string;
  dashActiveMachines: string;
  dashPendingUpdates: string;
  dashOpenTickets: string;
  dashMonthlyUsage: string;
  dashRecentActivity: string;
  dashNoActivity: string;
  dashViewAll: string;
  // ── Dashboard Tables ──
  dashColMachine: string;
  dashColStatus: string;
  dashColVersion: string;
  dashColDate: string;
  dashColAction: string;
  dashColAmount: string;
  dashColPlan: string;
  dashColPriority: string;
  dashColSubject: string;
  // ── Dashboard Buttons / Labels ──
  dashDownload: string;
  dashUpgrade: string;
  dashRenew: string;
  dashSubmit: string;
  dashSave: string;
  dashCancel: string;
  dashDelete: string;
  dashEdit: string;
  dashClose: string;
  dashRetry: string;
  dashLoading: string;
  dashNoData: string;
  dashError: string;
  dashEmptyState: string;
  dashMarkRead: string;
  dashAddRule: string;
  dashExportPdf: string;
  dashAddToCart: string;
  dashCheckout: string;
  dashClaimWarranty: string;
  dashRequestMaintenance: string;
  dashGenerateKey: string;
  // ── Dashboard Health ──
  dashHealthTitle: string;
  dashHealthScore: string;
  dashHealthGood: string;
  dashHealthWarning: string;
  dashHealthCritical: string;
  dashHealthAiBtn: string;
  dashHealthAiLoading: string;
  dashHealthAiFailed: string;
  // ── Dashboard Maintenance ──
  dashMaintenanceTitle: string;
  dashMaintenanceMachine: string;
  dashMaintenanceIssue: string;
  dashMaintenanceDate: string;
  dashMaintenanceTime: string;
  dashMaintenanceContact: string;
  dashMaintenanceAddress: string;
  dashMaintenanceSubmitBtn: string;
  dashMaintenanceSuccess: string;
  // ── Dashboard Contracts ──
  dashContractsTitle: string;
  dashContractNumber: string;
  dashContractStart: string;
  dashContractEnd: string;
  dashContractMachines: string;
  dashContractValue: string;
  dashContractDownload: string;
  dashContractExpiring: string;
  // ── Dashboard Analytics ──
  dashAnalyticsTitle: string;
  dashAnalyticsHoursDay: string;
  dashAnalyticsTotalMonth: string;
  dashAnalyticsAvgDaily: string;
  dashAnalyticsPeakTime: string;
  dashAnalyticsCostHour: string;
  dashAnalyticsExport: string;
  dashAnalyticsAiBtn: string;
  // ── Dashboard Shop ──
  dashShopTitle: string;
  dashShopCompatibility: string;
  dashShopPrice: string;
  dashShopCart: string;
  dashShopCheckout: string;
  dashShopOrderSuccess: string;
  // ── Dashboard Training ──
  dashTrainingTitle: string;
  dashTrainingVideo: string;
  dashTrainingManual: string;
  dashTrainingDoc: string;
  dashTrainingDownload: string;
  dashTrainingPlay: string;
  // ── Dashboard Warranty ──
  dashWarrantyTitle: string;
  dashWarrantyMachine: string;
  dashWarrantyPurchase: string;
  dashWarrantyExpiry: string;
  dashWarrantyCoverage: string;
  dashWarrantyClaim: string;
  dashWarrantyActive: string;
  dashWarrantyExpired: string;
  dashWarrantyExpiringSoon: string;
  // ── Dashboard Energy ──
  dashEnergyTitle: string;
  dashEnergyKwh: string;
  dashEnergyCost: string;
  dashEnergyVsLastMonth: string;
  dashEnergyAnnual: string;
  dashEnergyExport: string;
  dashEnergyAiBtn: string;
  // ── Dashboard Custom Alerts ──
  dashCustomAlertsTitle: string;
  dashCustomAlertsAdd: string;
  dashCustomAlertsCondition: string;
  dashCustomAlertsAction: string;
  dashCustomAlertsDelete: string;
  // ── Dashboard 2FA ──
  dash2FATitle: string;
  dash2FADesc: string;
  dash2FAEnable: string;
  dash2FADisable: string;
  dash2FAEnabled: string;
  dash2FADisabled: string;
  // ── Dashboard API ──
  dashApiTitle: string;
  dashApiKey: string;
  dashApiDocs: string;
  dashApiUsage: string;
  dashApiCalls: string;
  dashApiGenerate: string;
  // ── Dashboard Feedback ──
  dashFeedbackTitle: string;
  dashFeedbackAvg: string;
  dashFeedbackTotal: string;
  dashFeedbackNoData: string;
  // ── Dashboard Announcements ──
  dashAnnouncementsTitle: string;
  dashAnnouncementsMarkRead: string;
  dashAnnouncementsNoData: string;
  // ── Settings Page ──
  settingsAccount: string;
  settingsTitle: string;
  settingsEmail: string;
  settingsCurrentEmail: string;
  settingsNewEmail: string;
  settingsUpdateEmail: string;
  settingsUpdating: string;
  settingsPassword: string;
  settingsNewPassword: string;
  settingsConfirmPassword: string;
  settingsChangePassword: string;
  settingsPasswordMin: string;
  settingsPasswordMismatch: string;
  settings2FA: string;
  settings2FADesc: string;
  settings2FAEnabled: string;
  settings2FADisabled: string;
  settingsNotifications: string;
  settingsEmailNotif: string;
  settingsEmailNotifDesc: string;
  settingsSmsNotif: string;
  settingsSmsNotifDesc: string;
  settingsProductUpdates: string;
  settingsProductUpdatesDesc: string;
  settingsNewsletter: string;
  settingsNewsletterDesc: string;
  settingsLanguage: string;
  settingsDangerZone: string;
  settingsDeleteAccount: string;
  settingsDeleteDesc: string;
  settingsDeleteBtn: string;
  settingsDeleteConfirmTitle: string;
  settingsDeleteConfirmDesc: string;
  settingsDeleteConfirmType: string;
  settingsDeleteConfirmBtn: string;
  settingsDeleteConfirming: string;
  settingsCancel: string;
  settingsEmailConfirmSent: string;
  settingsEmailError: string;
  settingsPasswordUpdated: string;
  settingsPasswordError: string;
  settingsDarkMode: string;
  settingsDarkModeDesc: string;
  // ── Blog Page ──
  blogTitle: string;
  blogSubtitle: string;
  blogReadMore: string;
  blogSubscribe: string;
  blogSubscribePlaceholder: string;
  blogSubscribeBtn: string;
  blogSubscribeSuccess: string;
  blogCategory: string;
  blogDate: string;
  // ── Karriere Page ──
  karriereTitle: string;
  karriereSubtitle: string;
  karriereApply: string;
  karriereLocation: string;
  karriereDepartment: string;
  karriereApplyTitle: string;
  karriereNameLabel: string;
  karriereEmailLabel: string;
  karrierePhoneLabel: string;
  karriereCvLabel: string;
  karriereMessageLabel: string;
  karriereSubmitBtn: string;
  karriereSuccess: string;
  karriereNoJobs: string;
  // ── Partnere Page ──
  partnereTitle: string;
  partnereSubtitle: string;
  partnereCountry: string;
  partnereDescription: string;
  // ── Status Page ──
  statusTitle: string;
  statusSubtitle: string;
  statusOperational: string;
  statusOutage: string;
  statusDegraded: string;
  statusHistory: string;
  statusLastChecked: string;
  statusAllOperational: string;
  // ── Onboarding ──
  onboardingTitle: string;
  onboardingSubtitle: string;
  onboardingNext: string;
  onboardingBack: string;
  onboardingFinish: string;
  onboardingStep1Title: string;
  onboardingStep2Title: string;
  onboardingStep3Title: string;
  onboardingSelectPlan: string;
  onboardingCompanyLabel: string;
  onboardingIndustryLabel: string;
  onboardingMachineCount: string;
  onboardingComplete: string;
  // ── Faktura Page ──
  fakturaTitle: string;
  fakturaSubtitle: string;
  fakturaColDate: string;
  fakturaColAmount: string;
  fakturaColStatus: string;
  fakturaColPlan: string;
  fakturaDownload: string;
  fakturaNoData: string;
  fakturaPaid: string;
  fakturaPending: string;
  fakturaOverdue: string;
  // ── Common ──
  commonSave: string;
  commonCancel: string;
  commonDelete: string;
  commonEdit: string;
  commonClose: string;
  commonDownload: string;
  commonRetry: string;
  commonLoading: string;
  commonNoData: string;
  commonError: string;
  commonSearch: string;
  commonFilter: string;
  commonAll: string;
  commonSubmit: string;
  commonBack: string;
  commonNext: string;
  commonConfirm: string;
  // ── Toast Messages ──
  toastLoginSuccess: string;
  toastSignupSuccess: string;
  toastLogoutSuccess: string;
  toastSaveSuccess: string;
  toastDeleteSuccess: string;
  toastError: string;
  toastContactSuccess: string;
  toastNewsletterSuccess: string;
  toastMaintenanceSuccess: string;
  toastOrderSuccess: string;
  toastWarrantySuccess: string;
  toastAlertSaved: string;
  toastPasswordUpdated: string;
  toastEmailUpdated: string;
  // ── Browser Tab Titles ──
  tabHome: string;
  tabMachines: string;
  tabPricing: string;
  tabDashboard: string;
  tabSettings: string;
  tabFaktura: string;
  tabBlog: string;
  tabKarriere: string;
  tabPartnere: string;
  tabStatus: string;
  tabOnboarding: string;
  // ── New Visual Sections ──
  trustedByLabel: string;
  videoLabel: string;
  videoTitle: string;
  videoSubtitle: string;
  numbersLabel: string;
  testimonialsLabel: string;
  testimonialsTitle: string;
  mapLabel: string;
  mapTitle: string;
  mapSubtitle: string;
  // ── Billing Screen ──
  billingTitle: string;
  billingCurrentPlan: string;
  billingPlanStatus: string;
  billingMonthly: string;
  billingNextInvoice: string;
  billingDaysRemaining: string;
  billingUpgrade: string;
  billingDowngrade: string;
  billingUsageTitle: string;
  billingMachines: string;
  billingApiCalls: string;
  billingTeamMembers: string;
  billingStorage: string;
  billingInvoiceHistory: string;
  billingInvoiceNo: string;
  billingDate: string;
  billingPlan: string;
  billingAmount: string;
  billingStatus: string;
  billingDownloadPdf: string;
  billingNoInvoices: string;
  billingRecipientTitle: string;
  billingRecipientCompany: string;
  billingRecipientOrg: string;
  billingRecipientAddress: string;
  billingRecipientEmail: string;
  billingRecipientPhone: string;
  billingAddRecipient: string;
  billingEditRecipient: string;
  billingNoRecipient: string;
  billingPlanOptions: string;
  billingChoosePlan: string;
  billingCurrentBadge: string;
  billingStatusActive: string;
  billingStatusInactive: string;
  billingStatusTrial: string;
  billingStatusPaid: string;
  billingStatusPending: string;
  billingStatusOverdue: string;
  billingDowngradeTitle: string;
  billingDowngradeConfirm: string;
  billingDowngradeCancel: string;
  billingDowngradeWarning: string;
  tabFakturering: string;
}

const NO: Translations = {
  // ── Navigation ──
  navHome: 'Hjem',
  navMachines: 'Maskiner',
  navSubscription: 'Abonnement',
  navAbout: 'Om oss',
  navContact: 'Kontakt',
  navLogin: 'Logg inn',
  navSignup: 'Opprett konto',
  navDashboard: 'Dashboard',
  navInvoice: 'Faktura',
  navSettings: 'Innstillinger',
  navLogout: 'Logg ut',
  // ── Footer ──
  footerRights: '\u00a9 2021 SebNorge AS. Alle rettigheter forbeholdt.',
  footerLang: 'Norsk',
  footerBlog: 'Blogg',
  footerKarriere: 'Karriere',
  footerPartnere: 'Partnere',
  footerStatus: 'Status',
  // ── Homepage Hero ──
  heroLabel: 'Maskiner bygget for fremtiden',
  heroTagline: 'Industriell presisjon. Norsk ingeni\u00f8rkunst.',
  heroTitle: 'Industrielle maskiner for Norges fremtid',
  heroSubtitle: 'Avanserte l\u00f8sninger for moderne industri \u2014 levert med presisjon og p\u00e5litelighet.',
  heroCta: 'Utforsk maskiner',
  heroCtaSecondary: 'Opprett konto',
  heroScrollLabel: 'Scroll',
  // ── Homepage Stats ──
  statMachines: '15 000+',
  statCountries: '38',
  statYears: '12 \u00e5r',
  statUpdates: '142',
  statMachinesLabel: 'Maskiner',
  statCountriesLabel: 'Land',
  statYearsLabel: 'Erfaring',
  statUpdatesLabel: 'Oppdateringer',
  // ── Machine Sections ──
  machineSeriesLabel: 'Industriell Serie',
  machineHeavyLabel: 'Tung Maskin',
  machineTechLabel: 'Tech Hardware',
  machine1Name: 'Nordic Pro X7',
  machine1Desc: 'Neste generasjons industrielle kraftmaskin. Designet for kontinuerlig drift i krevende norske milj\u00f8er med uovertruffen presisjon.',
  machine1Spec1Label: 'Effekt',
  machine1Spec1Value: '380 kW',
  machine1Spec2Label: 'Versjon',
  machine1Spec2Value: 'v4.2.1',
  machine1Spec3Label: 'Driftstype',
  machine1Spec3Value: 'Kontinuerlig',
  machine1Spec4Label: 'Sertifisert',
  machine1Spec4Value: 'ISO 9001',
  machine1Cta: 'Se spesifikasjoner \u2192',
  machine2Name: 'Fjord 900T',
  machine2Desc: 'Tungvekter i sin klasse. 900 tonn kapasitet med hydraulisk presisjonsystem som setter ny standard for norsk tungindustri.',
  machine2Spec1Label: 'Kapasitet',
  machine2Spec1Value: '900 tonn',
  machine2Spec2Label: 'Versjon',
  machine2Spec2Value: 'v3.8.0',
  machine2Spec3Label: 'Hydraulikk',
  machine2Spec3Value: '340 bar',
  machine2Spec4Label: 'Sertifisert',
  machine2Spec4Value: 'CE / DNV',
  machine2Cta: 'Se spesifikasjoner \u2192',
  machine3Name: 'SebCore \u03a9',
  machine3Desc: 'SebNorges flaggskip prosessorenhet. 128 kjerner med kvantum-sikret kryptering og sanntids maskinl\u00e6ring integrert direkte i silisiumarkitekturen.',
  machine3Spec1Label: 'Kjerner',
  machine3Spec1Value: '128',
  machine3Spec2Label: 'Versjon',
  machine3Spec2Value: 'v5.0.3',
  machine3Spec3Label: 'Kryptering',
  machine3Spec3Value: 'Kvantum',
  machine3Spec4Label: 'TDP',
  machine3Spec4Value: '420W',
  machine3Cta: 'Se spesifikasjoner \u2192',
  // ── Software Updates Section ──
  updatesLabel: 'Programvare',
  updatesTitle: 'Siste oppdateringer',
  updatesViewAll: 'Alle oppdateringer \u2192',
  updateStatusStable: 'Stabil',
  updateStatusBeta: 'Beta',
  updateStatusMaintenance: 'Vedlikehold',
  updateStatusNew: 'Ny',
  // ── About Section ──
  aboutLabel: 'Om SebNorge',
  aboutTitle: 'Norsk presisjon.\nGlobal rekkevidde.',
  aboutBody1: 'SebNorge ble grunnlagt med \u00e9n misjon: \u00e5 bygge maskiner som overlever generasjoner. Siden 2014 har vi levert industriell presisjonsteknologi til 38 land, med et urokkelg fokus p\u00e5 norsk ingeni\u00f8rkunst og b\u00e6rekraftig innovasjon.',
  aboutBody2: 'Hver maskin gjennomg\u00e5r 847 kvalitetstester f\u00f8r levering. V\u00e5r programvareplattform oppdateres kontinuerlig \u2014 142 ganger siden lansering \u2014 og sikrer at hver maskin alltid kj\u00f8rer optimal firmware.',
  aboutQuote: '"Vi bygger fremtiden, \u00e9n maskin om gangen."',
  aboutCta: 'Utforsk maskinkatalogen \u2192',
  aboutContactCta: 'Kontakt oss',
  aboutStat1Label: 'Land',
  aboutStat2Label: 'I drift',
  aboutStat3Label: 'Oppetid',
  aboutStat4Label: 'Oppdateringer',
  // ── Contact Section ──
  contactTitle: 'Kontakt oss',
  contactSubtitle: 'Ta kontakt med oss for sp\u00f8rsm\u00e5l, tilbud eller support.',
  contactName: 'Navn',
  contactEmail: 'E-post',
  contactPhone: 'Telefon',
  contactMessage: 'Melding',
  contactSend: 'Send melding',
  contactSending: 'Sender...',
  contactSuccess: 'Melding sendt! Vi tar kontakt snart.',
  contactNamePlaceholder: 'Ditt navn',
  contactEmailPlaceholder: 'din@epost.no',
  contactPhonePlaceholder: '+47 000 00 000',
  contactMessagePlaceholder: 'Skriv din melding her...',
  contactLabel: 'Kontakt',
  contactHeading: 'Kontakt oss',
  contactBody: 'Vi er her for \u00e5 hjelpe deg med alle dine sp\u00f8rsm\u00e5l.',
  contactEmailLabel: 'E-post',
  contactPhoneLabel: 'Telefon',
  contactAddressLabel: 'Adresse',
  contactHoursLabel: '\u00c5pningstider',
  contactHoursValue: '9:00-17:00',
  contactFormName: 'Navn',
  contactFormNamePlaceholder: 'Ditt navn',
  contactFormEmail: 'E-post',
  contactFormEmailPlaceholder: 'din@epost.no',
  contactFormCompany: 'Bedrift',
  contactFormCompanyPlaceholder: 'Bedriftsnavn',
  contactFormMessage: 'Melding',
  contactFormMessagePlaceholder: 'Skriv din melding her...',
  contactFormSubmit: 'Send melding',
  contactToast: 'Melding sendt!',
  // ── Newsletter ──
  newsletterTitle: 'Hold deg oppdatert',
  newsletterSubtitle: 'Abonner p\u00e5 nyhetsbrevet v\u00e5rt for siste nyheter om maskiner og oppdateringer.',
  newsletterPlaceholder: 'Din e-postadresse',
  newsletterBtn: 'Abonner',
  newsletterSuccess: 'Du er n\u00e5 abonnent!',
  newsletterLabel: 'Nyhetsbrev',
  newsletterHeading: 'Abonner p\u00e4 nyhetsbrev',
  newsletterBody: 'Motta nyheter og oppdateringer fra oss.',
  newsletterEmailLabel: 'E-post',
  newsletterEmailPlaceholder: 'din@epost.no',
  newsletterPhoneLabel: 'Telefon',
  newsletterPhonePlaceholder: '+47 000 00 000',
  newsletterEmailToggle: 'E-post',
  newsletterSmsToggle: 'SMS',
  newsletterSubmit: 'Abonner',
  newsletterDisclaimer: 'Vi respekterer dine personlige rettigheter.',
  newsletterToast: 'Abonnement bekreftet!',
  newsletterCompanyPlaceholder: 'Bedriftsnavn AS',
  newsletterOrgPlaceholder: '123 456 789',
  newsletterAddressPlaceholder: 'Gateadresse, Postnummer Sted',
  // ── Machines Page ──
  machinesPageTitle: 'Maskinkatalog',
  machinesPageSubtitle: 'Utforsk hele v\u00e5rt utvalg av industrielle maskiner.',
  machinesCountLabel: 'maskiner',
  machinesSearchPlaceholder: 'S\u00f8k maskiner...',
  machinesFilterAll: 'Alle',
  machinesFilterIndustrial: 'Industriell',
  machinesFilterHeavy: 'Tung',
  machinesFilterTech: 'Tech',
  machinesFilterCNC: 'CNC',
  machinesColName: 'Maskin',
  machinesColCategory: 'Kategori',
  machinesColStatus: 'Status',
  machinesColVersion: 'Versjon',
  machinesColAction: 'Handling',
  machinesLearnMore: 'Les mer',
  machinesViewAll: 'Se alle maskiner',
  machinesAddToCart: 'Legg i handlekurv',
  machinesPagination: 'Viser',
  machinesStatusActive: 'Aktiv',
  machinesStatusBeta: 'Beta',
  machinesStatusMaintenance: 'Vedlikehold',
  // ── Pricing Page ──
  pricingTitle: 'Abonnement',
  pricingSubtitle: 'Velg planen som passer din bedrift.',
  pricingMonthly: 'M\u00e5nedlig',
  pricingAnnual: '\u00c5rlig',
  pricingGetStarted: 'Kom i gang',
  pricingContactSales: 'Kontakt salg',
  pricingMostPopular: 'Mest popul\u00e6r',
  pricingPlan1Name: 'Gratis',
  pricingPlan1Price: '0',
  pricingPlan1Desc: 'Perfekt for \u00e5 komme i gang',
  pricingPlan2Name: 'Basis',
  pricingPlan2Price: '499',
  pricingPlan2Desc: 'For sm\u00e5 og mellomstore bedrifter',
  pricingPlan3Name: 'Pro',
  pricingPlan3Price: '1 499',
  pricingPlan3Desc: 'For voksende virksomheter',
  pricingPlan4Name: 'Enterprise',
  pricingPlan4Price: 'Tilpasset',
  pricingPlan4Desc: 'For store organisasjoner',
  pricingTrustBadge1: 'Ingen bindingstid',
  pricingTrustBadge2: 'Avbryt n\u00e5r som helst',
  pricingTrustBadge3: '24/7 support',
  pricingPerMonth: '/ mnd',
  // ── Auth Modal ──
  authLoginTitle: 'Logg inn',
  authSignupTitle: 'Opprett konto',
  authEmailLabel: 'E-postadresse',
  authPasswordLabel: 'Passord',
  authNameLabel: 'Fullt navn',
  authCompanyLabel: 'Bedrift',
  authPhoneLabel: 'Telefonnummer',
  authLoginBtn: 'Logg inn',
  authSignupBtn: 'Opprett konto',
  authForgotPassword: 'Glemt passord?',
  authNoAccount: 'Har du ikke konto?',
  authHaveAccount: 'Har du allerede konto?',
  authDividerText: 'eller',
  authSocialBtn: 'Fortsett med Google',
  authEmailOptIn: 'Jeg \u00f8nsker \u00e5 motta e-postvarsler',
  authSmsOptIn: 'Jeg \u00f8nsker \u00e5 motta SMS-varsler',
  authLoginSuccess: 'Innlogging vellykket \u2014 velkommen tilbake!',
  authSignupSuccess: 'Konto opprettet! Sjekk telefonen for en velkomst-SMS.',
  authLoginError: 'Innlogging mislyktes. Sjekk e-post og passord.',
  authSignupError: 'Registrering mislyktes. Pr\u00f8v igjen.',
  authNamePlaceholder: 'Ola Nordmann',
  authEmailPlaceholder: 'din@epost.no',
  authPasswordPlaceholder: 'Minst 8 tegn',
  authCompanyPlaceholder: 'Bedriftsnavn AS',
  authPhonePlaceholder: '+47 000 00 000',
  // ── Invoice Modal ──
  invoiceTitle: 'Fakturaopplysninger',
  invoiceCompanyLabel: 'Bedriftsnavn',
  invoiceOrgLabel: 'Organisasjonsnummer',
  invoiceAddressLabel: 'Fakturaadresse',
  invoiceEmailLabel: 'Faktura-e-post',
  invoicePlanLabel: 'Valgt plan',
  invoicePeriodLabel: 'Periode',
  invoiceAmountLabel: 'Bel\u00f8p',
  invoiceSubmitBtn: 'Send faktura',
  invoiceSuccess: 'Faktura sendt til din e-postadresse.',
  invoiceCompanyPlaceholder: 'Bedriftsnavn AS',
  invoiceOrgPlaceholder: '123 456 789',
  invoiceAddressPlaceholder: 'Gateadresse, Postnummer Sted',
  invoiceEmailPlaceholder: 'faktura@bedrift.no',
  // ── Dashboard ──
  dashboardTitle: 'Dashboard',
  dashboardWelcome: 'Velkommen tilbake',
  dashboardMachines: 'Dine maskiner',
  dashboardUpdates: 'Siste oppdateringer',
  dashboardInvoices: 'Fakturaer & Betaling',
  dashboardSupport: 'Supportbilletter',
  dashboardAlerts: 'Varsler & Alarmer',
  dashboardNoMachines: 'Ingen maskiner registrert.',
  dashboardNoUpdates: 'Ingen oppdateringer.',
  dashboardNoInvoices: 'Ingen fakturaer.',
  dashboardNoAlerts: 'Ingen aktive varsler.',
  dashboardViewAll: 'Se alle',
  dashboardMachineStatus: 'Status',
  dashboardMachineModel: 'Modell',
  dashboardMachineSerial: 'Serienummer',
  dashboardMachineLocation: 'Plassering',
  dashboardSoftwareUpdates: 'Programvareoppdateringer',
  dashboardServicePlan: 'Serviceplan',
  dashboardPerformance: 'Ytelsesrapporter',
  dashboardSavedMachines: 'Lagrede maskiner',
  dashboardParts: 'Deler & Tilbeh\u00f8r',
  dashboardTraining: 'Oppl\u00e6ring & Dokumentasjon',
  dashboardFeedback: 'Tilbakemeldinger',
  dashboardAnnouncements: 'Kunngj\u00f8ringer',
  // ── Machine Catalog ──
  machinesCatalogTitle: 'Maskinkataloget',
  machinesCatalogSubtitle: 'Utforsk vårt utvalg av industrielle maskiner.',
  machinesSearch: 'Søk maskiner...',
  machinesFilterCategory: 'Kategori',
  machinesFilterStatus: 'Status',
  machinesColName: 'Navn',
  machinesColCategory: 'Kategori',
  machinesColStatus: 'Status',
  machinesColPrice: 'Pris',
  machinesColActions: 'Handlinger',
  machinesViewDetails: 'Se detaljer',
  machinesNoResults: 'Ingen maskiner funnet.',
  machinesStatusAvailable: 'Tilgjengelig',
  machinesStatusUnavailable: 'Utilgjengelig',
  machinesStatusMaintenance: 'Vedlikehold',
  // ── Machine Detail ──
  machineDetailSpecs: 'Spesifikasjoner',
  machineDetailDescription: 'Beskrivelse',
  machineDetailPrice: 'Pris',
  machineDetailStatus: 'Status',
  machineDetailCategory: 'Kategori',
  machineDetailBack: 'Tilbake til katalog',
  machineDetailContact: 'Kontakt oss om denne maskinen',
  machineDetailAiInsights: 'AI Innsikt',
  machineDetailAiPerformance: 'Ytelsesanbefalinger',
  machineDetailAiMaintenance: 'Vedlikeholdsprediksjoner',
  machineDetailAiCost: 'Kostnadsoptimalisering',
  machineDetailAiGenerate: 'Generer',
  machineDetailAiCopy: 'Kopier',
  machineDetailAiRetry: 'Prøv igjen',
  // ── Settings Page ──
  settingsTitle: 'Innstillinger',
  settingsProfile: 'Profil',
  settingsSecurity: 'Sikkerhet',
  settingsNotifications: 'Varsler',
  settingsNameLabel: 'Navn',
  settingsEmailLabel: 'E-post',
  settingsPhoneLabel: 'Telefon',
  settingsCompanyLabel: 'Firma',
  settingsSaveBtn: 'Lagre endringer',
  settingsCurrentPassword: 'Nåværende passord',
  settingsNewPassword: 'Nytt passord',
  settingsConfirmPassword: 'Bekreft passord',
  settingsUpdatePassword: 'Oppdater passord',
  settingsEmailNotif: 'E-post varsler',
  settingsSmsNotif: 'SMS varsler',
  settingsPushNotif: 'Push-varsler',
  settingsCancel: 'Avbryt',
  settingsEmailConfirmSent: 'Bekreftelseslenke sendt til ny e-postadresse.',
  settingsEmailError: 'Feil ved oppdatering av e-post.',
  settingsPasswordUpdated: 'Passord oppdatert.',
  settingsPasswordError: 'Feil ved oppdatering av passord.',
  settingsDarkMode: 'M\u00f8rkt modus',
  settingsDarkModeDesc: 'Bytt mellom m\u00f8rkt og lyst tema',
  // ── Blog Page ──
  blogTitle: 'Blogg & Nyheter',
  blogSubtitle: 'Siste nytt fra SebNorge \u2014 maskiner, oppdateringer og bransjenyheter.',
  blogReadMore: 'Les mer',
  blogSubscribe: 'Abonner p\u00e4 nyhetsbrev',
  blogSubscribePlaceholder: 'Din e-postadresse',
  blogSubscribeBtn: 'Abonner',
  blogSubscribeSuccess: 'Du er n\u00e5 abonnent!',
  blogCategory: 'Kategori',
  blogDate: 'Dato',
  // ── Karriere Page ──
  karriereTitle: 'Karriere hos SebNorge',
  karriereSubtitle: 'Bli en del av teamet v\u00e5rt og v\u00e6r med \u00e5 forme fremtidens industri.',
  karriereApply: 'S\u00f8k n\u00e5',
  karriereLocation: 'Sted',
  karriereDepartment: 'Avdeling',
  karriereApplyTitle: 'S\u00f8k p\u00e5 stilling',
  karriereNameLabel: 'Fullt navn',
  karriereEmailLabel: 'E-postadresse',
  karrierePhoneLabel: 'Telefonnummer',
  karriereCvLabel: 'Last opp CV',
  karriereMessageLabel: 'Melding',
  karriereSubmitBtn: 'Send s\u00f8knad',
  karriereSuccess: 'S\u00f8knad sendt! Vi tar kontakt snart.',
  karriereNoJobs: 'Ingen ledige stillinger for \u00f8yeblikket.',
  // ── Partnere Page ──
  partnereTitle: 'V\u00e5re partnere',
  partnereSubtitle: 'SebNorges teknologi- og distribusjonspartnere i Norge og Europa.',
  partnereCountry: 'Land',
  partnereDescription: 'Beskrivelse',
  // ── Status Page ──
  statusTitle: 'Systemstatus',
  statusSubtitle: 'Sanntidsstatus for alle SebNorge-tjenester.',
  statusOperational: 'Operativ',
  statusOutage: 'Nedetid',
  statusDegraded: 'Degradert',
  statusHistory: '90-dagers historikk',
  statusLastChecked: 'Sist sjekket',
  statusAllOperational: 'Alle systemer operative',
  // ── Onboarding ──
  onboardingTitle: 'Velkommen til SebNorge',
  onboardingSubtitle: 'La oss sette opp kontoen din.',
  onboardingNext: 'Neste',
  onboardingBack: 'Tilbake',
  onboardingFinish: 'Fullf\u00f8r',
  onboardingStep1Title: 'Velg din plan',
  onboardingStep2Title: 'Om bedriften din',
  onboardingStep3Title: 'Bekreft og start',
  onboardingSelectPlan: 'Velg plan',
  onboardingCompanyLabel: 'Bedriftsnavn',
  onboardingIndustryLabel: 'Bransje',
  onboardingMachineCount: 'Antall maskiner',
  onboardingComplete: 'Konto konfigurert!',
  // ── Faktura Page ──
  fakturaTitle: 'Fakturaer',
  fakturaSubtitle: 'Oversikt over alle dine fakturaer.',
  fakturaColDate: 'Dato',
  fakturaColAmount: 'Bel\u00f8p',
  fakturaColStatus: 'Status',
  fakturaColPlan: 'Plan',
  fakturaDownload: 'Last ned',
  fakturaNoData: 'Ingen fakturaer funnet.',
  fakturaPaid: 'Betalt',
  fakturaPending: 'Venter',
  fakturaOverdue: 'Forfalt',
  // ── Common ──
  commonSave: 'Lagre',
  commonCancel: 'Avbryt',
  commonDelete: 'Slett',
  commonEdit: 'Rediger',
  commonClose: 'Lukk',
  commonDownload: 'Last ned',
  commonRetry: 'Pr\u00f8v igjen',
  commonLoading: 'Laster...',
  commonNoData: 'Ingen data tilgjengelig.',
  commonError: 'Noe gikk galt. Pr\u00f8v igjen.',
  commonSearch: 'S\u00f8k',
  commonFilter: 'Filter',
  commonAll: 'Alle',
  commonSubmit: 'Send',
  commonBack: 'Tilbake',
  commonNext: 'Neste',
  commonConfirm: 'Bekreft',
  // ── Toast Messages ──
  toastLoginSuccess: 'Innlogging vellykket \u2014 velkommen tilbake!',
  toastSignupSuccess: 'Konto opprettet! Sjekk telefonen for en velkomst-SMS.',
  toastLogoutSuccess: 'Du er n\u00e5 logget ut.',
  toastSaveSuccess: 'Endringer lagret.',
  toastDeleteSuccess: 'Slettet.',
  toastError: 'Noe gikk galt. Pr\u00f8v igjen.',
  toastContactSuccess: 'Melding sendt! We will be in touch soon.',
  toastNewsletterSuccess: 'You are now subscribed!',
  toastMaintenanceSuccess: 'Maintenance request submitted!',
  toastOrderSuccess: 'Order received! Invoice sent by email.',
  toastWarrantySuccess: 'Warranty claim submitted.',
  toastAlertSaved: 'Alert saved.',
  toastPasswordUpdated: 'Password updated.',
  toastEmailUpdated: 'Email updated.',
  // ── Browser Tab Titles ──
  tabHome: 'SebNorge \u2014 Industrielle maskiner',
  tabMachines: 'Maskiner \u2014 SebNorge',
  tabPricing: 'Abonnement \u2014 SebNorge',
  tabDashboard: 'Dashboard \u2014 SebNorge',
  tabSettings: 'Innstillinger \u2014 SebNorge',
  tabFaktura: 'Faktura \u2014 SebNorge',
  tabBlog: 'Blogg \u2014 SebNorge',
  tabKarriere: 'Karriere \u2014 SebNorge',
  tabPartnere: 'Partnere \u2014 SebNorge',
  tabStatus: 'Systemstatus \u2014 SebNorge',
  tabOnboarding: 'Kom i gang \u2014 SebNorge',
  // ── New Visual Sections ──
  trustedByLabel: 'Brukt av selskaper i 38 land',
  videoLabel: 'Se oss i aksjon',
  videoTitle: 'Se SebNorge i aksjon',
  videoSubtitle: 'Se hvordan maskinene v\u00e5re presterer i ekte industrielle milj\u00f8er.',
  numbersLabel: 'V\u00e5re tall',
  testimonialsLabel: 'Kundeuttalelser',
  testimonialsTitle: 'Hva kundene sier',
  mapLabel: 'Global rekkevidde',
  mapTitle: '38 land. \u00c9n kvalitetsstandard.',
  mapSubtitle: 'SebNorge leverer industriell presisjon til kunder p\u00e5 tvers av 38 land og 6 kontinenter.',
  // ── Billing Screen ──
  billingTitle: 'Fakturering',
  billingCurrentPlan: 'N\u00e5v\u00e6rende plan',
  billingPlanStatus: 'Planstatus',
  billingMonthly: 'M\u00e5nedlig fakturering',
  billingNextInvoice: 'Neste faktura',
  billingDaysRemaining: 'dager igjen',
  billingUpgrade: 'Oppgrader',
  billingDowngrade: 'Nedgrader',
  billingUsageTitle: 'Bruksstatistikk',
  billingMachines: 'Maskiner tilgjengelig',
  billingApiCalls: 'API-kall denne m\u00e5neden',
  billingTeamMembers: 'Aktive teammedlemmer',
  billingStorage: 'Datalagring brukt',
  billingInvoiceHistory: 'Fakturahistorikk',
  billingInvoiceNo: 'Fakturanr',
  billingDate: 'Dato',
  billingPlan: 'Plan',
  billingAmount: 'Bel\u00f8p',
  billingStatus: 'Status',
  billingDownloadPdf: 'Last ned PDF',
  billingNoInvoices: 'Ingen fakturaer enn\u00e5.',
  billingRecipientTitle: 'Fakturamottaker',
  billingRecipientCompany: 'Firmanavn',
  billingRecipientOrg: 'Org.nr',
  billingRecipientAddress: 'Adresse',
  billingRecipientEmail: 'E-post',
  billingRecipientPhone: 'Telefon',
  billingAddRecipient: '+ Legg til fakturamottaker',
  billingEditRecipient: 'Rediger',
  billingNoRecipient: 'Ingen fakturamottaker registrert.',
  billingPlanOptions: 'Planvalg',
  billingChoosePlan: 'Velg plan',
  billingCurrentBadge: 'N\u00e5v\u00e6rende',
  billingStatusActive: 'Aktiv',
  billingStatusInactive: 'Inaktiv',
  billingStatusTrial: 'Pr\u00f8veperiode',
  billingStatusPaid: 'Betalt',
  billingStatusPending: 'Venter',
  billingStatusOverdue: 'Forfalt',
  billingDowngradeTitle: 'Nedgrader plan',
  billingDowngradeConfirm: 'Bekreft nedgradering',
  billingDowngradeCancel: 'Avbryt',
  billingDowngradeWarning: 'Du vil miste tilgang til premium-funksjoner ved neste faktureringsdato.',
  tabFakturering: 'Fakturering \u2014 SebNorge',
}

const EN: Translations = {
  // ── Navigation ──
  navHome: 'Home',
  navMachines: 'Machines',
  navSubscription: 'Subscription',
  navAbout: 'About',
  navContact: 'Contact',
  navLogin: 'Log in',
  navSignup: 'Create account',
  navDashboard: 'Dashboard',
  navInvoice: 'Invoice',
  navSettings: 'Settings',
  navLogout: 'Log out',
  // ── Footer ──
  footerRights: '© 2021 SebNorge AS. All rights reserved.',
  footerLang: 'English',
  footerBlog: 'Blog',
  footerKarriere: 'Careers',
  footerPartnere: 'Partners',
  footerStatus: 'Status',
  // ── Homepage Hero ──
  heroLabel: 'Machines built for the future',
  heroTagline: 'Industrial precision. Norwegian engineering.',
  heroTitle: 'Industrial machines for Norway\'s future',
  heroSubtitle: 'Advanced solutions for modern industry — delivered with precision and reliability.',
  heroCta: 'Explore machines',
  heroCtaSecondary: 'Create account',
  heroScrollLabel: 'Scroll',
  // ── Homepage Stats ──
  statMachines: '15,000+',
  statCountries: '38',
  statYears: '12 yrs',
  statUpdates: '142',
  statMachinesLabel: 'Machines',
  statCountriesLabel: 'Countries',
  statYearsLabel: 'Experience',
  statUpdatesLabel: 'Updates',
  // ── Machine Sections ──
  machineSeriesLabel: 'Industrial Series',
  machineHeavyLabel: 'Heavy Machine',
  machineTechLabel: 'Tech Hardware',
  machine1Name: 'Nordic Pro X7',
  machine1Desc: 'Next-generation industrial powerhouse. Designed for continuous operation in demanding Norwegian environments with unmatched precision.',
  machine1Spec1Label: 'Power',
  machine1Spec1Value: '380 kW',
  machine1Spec2Label: 'Version',
  machine1Spec2Value: 'v4.2.1',
  machine1Spec3Label: 'Operation',
  machine1Spec3Value: 'Continuous',
  machine1Spec4Label: 'Certified',
  machine1Spec4Value: 'ISO 9001',
  machine1Cta: 'View specifications →',
  machine2Name: 'Fjord 900T',
  machine2Desc: 'Tungvekter i sin klasse. 900-ton capacity with hydraulic precision system setting a new standard for Norwegian heavy industry.',
  machine2Spec1Label: 'Capacity',
  machine2Spec1Value: '900 tons',
  machine2Spec2Label: 'Version',
  machine2Spec2Value: 'v3.8.0',
  machine2Spec3Label: 'Hydraulics',
  machine2Spec3Value: '340 bar',
  machine2Spec4Label: 'Certified',
  machine2Spec4Value: 'CE / DNV',
  machine2Cta: 'View specifications →',
  machine3Name: 'SebCore Ω',
  machine3Desc: 'SebNorges flaggskip prosessorenhet. 128 cores with quantum-secured encryption and sanntids maskinlæring integrert direkte i silisiumarkitekturen.',
  machine3Spec1Label: 'Cores',
  machine3Spec1Value: '128',
  machine3Spec2Label: 'Version',
  machine3Spec2Value: 'v5.0.3',
  machine3Spec3Label: 'Encryption',
  machine3Spec3Value: 'Quantum',
  machine3Spec4Label: 'TDP',
  machine3Spec4Value: '420W',
  machine3Cta: 'View specifications →',
  // ── Software Updates Section ──
  updatesLabel: 'Software',
  updatesTitle: 'Latest updates',
  updatesViewAll: 'All updates →',
  updateStatusStable: 'Stable',
  updateStatusBeta: 'Beta',
  updateStatusMaintenance: 'Maintenance',
  updateStatusNew: 'New',
  // ── About Section ──
  aboutLabel: 'About SebNorge',
  aboutTitle: 'Norwegian precision.\nGlobal reach.',
  aboutBody1: 'SebNorge was founded with one mission: to build machines that last generations. Since 2014 we have delivered industrial precision technology to 38 countries, with an unwavering focus on Norwegian engineering and sustainable innovation.',
  aboutBody2: 'Every machine undergoes 847 quality tests before delivery. Our software platform is continuously updated — 142 times since launch — ensuring every machine always runs optimal firmware.',
  aboutQuote: '"We build the future, one machine at a time."',
  aboutCta: 'Explore the machine catalogue →',
  aboutContactCta: 'Contact us',
  aboutStat1Label: 'Countries',
  aboutStat2Label: 'In operation',
  aboutStat3Label: 'Uptime',
  aboutStat4Label: 'Updates',
  // ── Contact Section ──
  contactTitle: 'Contact us',
  contactSubtitle: 'Get in touch for questions, quotes or support.',
  contactName: 'Name',
  contactEmail: 'Email',
  contactPhone: 'Phone',
  contactMessage: 'Message',
  contactSend: 'Send message',
  contactSending: 'Sending...',
  contactSuccess: 'Message sent! We will be in touch soon.',
  contactNamePlaceholder: 'Your name',
  contactEmailPlaceholder: 'your@email.com',
  contactPhonePlaceholder: '+47 000 00 000',
  contactMessagePlaceholder: 'Write your message here...',
  // ── Contact Section (extended) ──
  contactLabel: 'Contact',
  contactHeading: 'Contact us',
  contactBody: 'We are here to help you with all your questions.',
  contactEmailLabel: 'Email',
  contactPhoneLabel: 'Phone',
  contactAddressLabel: 'Address',
  contactHoursLabel: 'Opening hours',
  contactHoursValue: '9:00-17:00',
  contactFormName: 'Name',
  contactFormNamePlaceholder: 'Your name',
  contactFormEmail: 'Email',
  contactFormEmailPlaceholder: 'your@email.com',
  contactFormCompany: 'Company',
  contactFormCompanyPlaceholder: 'Company name',
  contactFormMessage: 'Message',
  contactFormMessagePlaceholder: 'Write your message here...',
  contactFormSubmit: 'Send message',
  contactToast: 'Message sent!',
  // ── Newsletter ──
  newsletterTitle: 'Stay updated',
  newsletterSubtitle: 'Subscribe to our newsletter for the latest news on machines and updates.',
  newsletterPlaceholder: 'Your email address',
  newsletterBtn: 'Subscribe',
  newsletterSuccess: 'You are now subscribed!',
  // ── Newsletter (extended) ──
  newsletterLabel: 'Newsletter',
  newsletterHeading: 'Subscribe to our newsletter',
  newsletterBody: 'Receive news and updates from us.',
  newsletterEmailLabel: 'Email',
  newsletterEmailPlaceholder: 'your@email.com',
  newsletterPhoneLabel: 'Phone',
  newsletterPhonePlaceholder: '+47 000 00 000',
  newsletterEmailToggle: 'Email',
  newsletterSmsToggle: 'SMS',
  newsletterSubmit: 'Subscribe',
  newsletterDisclaimer: 'We respect your personal rights.',
  newsletterToast: 'Subscription confirmed!',
  // ── Machines Page ──
  machinesPageTitle: 'Machine catalogue',
  machinesPageSubtitle: 'Explore our full range of industrial machines.',
  machinesCountLabel: 'machines',
  machinesSearchPlaceholder: 'Search machines...',
  machinesFilterAll: 'All',
  machinesFilterIndustrial: 'Industrial',
  machinesFilterHeavy: 'Heavy',
  machinesFilterTech: 'Tech',
  machinesFilterCNC: 'CNC',
  machinesColName: 'Machine',
  machinesColCategory: 'Category',
  machinesColStatus: 'Status',
  machinesColVersion: 'Version',
  machinesColAction: 'Action',
  machinesLearnMore: 'Learn more',
  machinesViewAll: 'View all machines',
  machinesAddToCart: 'Add to cart',
  machinesPagination: 'Showing',
  machinesStatusActive: 'Active',
  machinesStatusBeta: 'Beta',
  machinesStatusMaintenance: 'Maintenance',
  // ── Pricing Page ──
  pricingTitle: 'Subscription',
  pricingSubtitle: 'Choose the plan that fits your business.',
  pricingMonthly: 'Monthly',
  pricingAnnual: 'Annual',
  pricingGetStarted: 'Get started',
  pricingContactSales: 'Contact sales',
  pricingMostPopular: 'Most popular',
  pricingPlan1Name: 'Free',
  pricingPlan1Price: '0',
  pricingPlan1Desc: 'Perfect to get started',
  pricingPlan2Name: 'Basic',
  pricingPlan2Price: '499',
  pricingPlan2Desc: 'For small and medium businesses',
  pricingPlan3Name: 'Pro',
  pricingPlan3Price: '1,499',
  pricingPlan3Desc: 'For growing businesses',
  pricingPlan4Name: 'Enterprise',
  pricingPlan4Price: 'Custom',
  pricingPlan4Desc: 'For large organisations',
  pricingTrustBadge1: 'No commitment',
  pricingTrustBadge2: 'Cancel anytime',
  pricingTrustBadge3: '24/7 support',
  pricingPerMonth: '/ mo',
  // ── Auth Modal ──
  authLoginTitle: 'Log in',
  authSignupTitle: 'Create account',
  authEmailLabel: 'Email address',
  authPasswordLabel: 'Password',
  authNameLabel: 'Full name',
  authCompanyLabel: 'Company',
  authPhoneLabel: 'Phone number',
  authLoginBtn: 'Log in',
  authSignupBtn: 'Create account',
  authForgotPassword: 'Forgot password?',
  authNoAccount: "Don't have an account?",
  authHaveAccount: 'Already have an account?',
  authDividerText: 'or',
  authSocialBtn: 'Continue with Google',
  authEmailOptIn: 'I want to receive email notifications',
  authSmsOptIn: 'I want to receive SMS notifications',
  authLoginSuccess: 'Login successful — welcome back!',
  authSignupSuccess: 'Account created! Check your phone for a welcome SMS.',
  authLoginError: 'Login failed. Check your email and password.',
  authSignupError: 'Registration failed. Please try again.',
  authNamePlaceholder: 'John Smith',
  authEmailPlaceholder: 'your@email.com',
  authPasswordPlaceholder: 'At least 8 characters',
  authCompanyPlaceholder: 'Company Name Ltd',
  authPhonePlaceholder: '+47 000 00 000',
  // ── Invoice Modal ──
  invoiceTitle: 'Invoice details',
  invoiceCompanyLabel: 'Company name',
  invoiceOrgLabel: 'Organisation number',
  invoiceAddressLabel: 'Invoice address',
  invoiceEmailLabel: 'Invoice email',
  invoicePlanLabel: 'Selected plan',
  invoicePeriodLabel: 'Period',
  invoiceAmountLabel: 'Amount',
  invoiceSubmitBtn: 'Send invoice',
  invoiceSuccess: 'Invoice sent to your email address.',
  invoiceCompanyPlaceholder: 'Company Name Ltd',
  invoiceOrgPlaceholder: '123 456 789',
  invoiceAddressPlaceholder: 'Street address, Postcode City',
  invoiceEmailPlaceholder: 'invoice@company.com',
  // ── Dashboard Sidebar ──
  dashOverview: 'Overview',
  dashMachines: 'My machines',
  dashUpdates: 'Software updates',
  dashServicePlan: 'Service plan',
  dashInvoices: 'Invoices & Payment',
  dashSupport: 'Support tickets',
  dashPerformance: 'Performance reports',
  dashTeam: 'Team & Access',
  dashSaved: 'Saved machines',
  dashAlerts: 'Alerts & Alarms',
  dashAccount: 'Account settings',
  dashHealth: 'Machine health monitor',
  dashMaintenance: 'Maintenance request',
  dashContracts: 'Contract manager',
  dashAnalytics: 'Usage analytics',
  dashShop: 'Parts & Accessories',
  dashTraining: 'Training & Documentation',
  dashWarranty: 'Warranty tracker',
  dashEnergy: 'Energy report',
  dashCustomAlerts: 'Custom alerts',
  dash2FA: 'Two-factor authentication',
  dashApi: 'API access',
  dashFeedback: 'Feedback & Reviews',
  dashAnnouncements: 'Announcements',
  dashDarkMode: 'Dark / Light mode',
  // ── Dashboard Overview ──
  dashOverviewTitle: 'Overview',
  dashWelcome: 'Welcome back',
  dashActiveMachines: 'Active machines',
  dashPendingUpdates: 'Pending updates',
  dashOpenTickets: 'Open tickets',
  dashMonthlyUsage: 'Monthly usage',
  dashRecentActivity: 'Recent activity',
  dashNoActivity: 'No recent activity.',
  dashViewAll: 'View all',
  // ── Dashboard Tables ──
  dashColMachine: 'Machine',
  dashColStatus: 'Status',
  dashColVersion: 'Version',
  dashColDate: 'Date',
  dashColAction: 'Action',
  dashColAmount: 'Amount',
  dashColPlan: 'Plan',
  dashColPriority: 'Priority',
  dashColSubject: 'Subject',
  // ── Dashboard Buttons / Labels ──
  dashDownload: 'Download',
  dashUpgrade: 'Upgrade',
  dashRenew: 'Renew',
  dashSubmit: 'Submit',
  dashSave: 'Save',
  dashCancel: 'Cancel',
  dashDelete: 'Delete',
  dashEdit: 'Edit',
  dashClose: 'Close',
  dashRetry: 'Try again',
  dashLoading: 'Loading...',
  dashNoData: 'No data available.',
  dashError: 'Something went wrong. Please try again.',
  dashEmptyState: 'No items to display.',
  dashMarkRead: 'Mark as read',
  dashAddRule: 'Add rule',
  dashExportPdf: 'Export PDF',
  dashAddToCart: 'Add to cart',
  dashCheckout: 'Checkout',
  dashClaimWarranty: 'Claim warranty',
  dashRequestMaintenance: 'Request maintenance',
  dashGenerateKey: 'Generate key',
  // ── Dashboard Health ──
  dashHealthTitle: 'Machine health monitor',
  dashHealthScore: 'Health score',
  dashHealthGood: 'Good',
  dashHealthWarning: 'Warning',
  dashHealthCritical: 'Critical',
  dashHealthAiBtn: 'AI Analysis',
  dashHealthAiLoading: 'Analysing...',
  dashHealthAiFailed: 'AI analysis failed. Please try again.',
  // ── Dashboard Maintenance ──
  dashMaintenanceTitle: 'Maintenance request',
  dashMaintenanceMachine: 'Machine name',
  dashMaintenanceIssue: 'Issue description',
  dashMaintenanceDate: 'Preferred date',
  dashMaintenanceTime: 'Preferred time',
  dashMaintenanceContact: 'On-site contact person',
  dashMaintenanceAddress: 'Address',
  dashMaintenanceSubmitBtn: 'Submit request',
  dashMaintenanceSuccess: 'Request submitted! We will confirm within 2 hours.',
  // ── Dashboard Contracts ──
  dashContractsTitle: 'Contract manager',
  dashContractNumber: 'Contract number',
  dashContractStart: 'Start date',
  dashContractEnd: 'End date',
  dashContractMachines: 'Machines',
  dashContractValue: 'Monthly value',
  dashContractDownload: 'Download PDF',
  dashContractExpiring: 'Expiring soon',
  // ── Dashboard Analytics ──
  dashAnalyticsTitle: 'Usage analytics',
  dashAnalyticsHoursDay: 'Hours per day this week',
  dashAnalyticsTotalMonth: 'Total hours this month',
  dashAnalyticsAvgDaily: 'Average daily usage',
  dashAnalyticsPeakTime: 'Peak usage time',
  dashAnalyticsCostHour: 'Estimated cost per hour',
  dashAnalyticsExport: 'Export report',
  dashAnalyticsAiBtn: 'AI Performance summary',
  // ── Dashboard Shop ──
  dashShopTitle: 'Parts & Accessories',
  dashShopCompatibility: 'Compatibility',
  dashShopPrice: 'Price',
  dashShopCart: 'Cart',
  dashShopCheckout: 'Checkout',
  dashShopOrderSuccess: 'Order received! Invoice sent by email.',
  // ── Dashboard Training ──
  dashTrainingTitle: 'Training & Documentation',
  dashTrainingVideo: 'Video',
  dashTrainingManual: 'Manual',
  dashTrainingDoc: 'Document',
  dashTrainingDownload: 'Download',
  dashTrainingPlay: 'Play',
  // ── Dashboard Warranty ──
  dashWarrantyTitle: 'Warranty tracker',
  dashWarrantyMachine: 'Machine',
  dashWarrantyPurchase: 'Purchase date',
  dashWarrantyExpiry: 'Warranty expiry',
  dashWarrantyCoverage: 'Coverage',
  dashWarrantyClaim: 'Claim warranty',
  dashWarrantyActive: 'Active',
  dashWarrantyExpired: 'Expired',
  dashWarrantyExpiringSoon: 'Expiring soon',
  // ── Dashboard Energy ──
  dashEnergyTitle: 'Energy report',
  dashEnergyKwh: 'kWh this month',
  dashEnergyCost: 'Estimated cost',
  dashEnergyVsLastMonth: 'vs last month',
  dashEnergyAnnual: 'Estimated annual cost',
  dashEnergyExport: 'Download energy report',
  dashEnergyAiBtn: 'AI Energy optimisation',
  // ── Dashboard Custom Alerts ──
  dashCustomAlertsTitle: 'Custom alerts',
  dashCustomAlertsAdd: 'Add alert',
  dashCustomAlertsCondition: 'Condition',
  dashCustomAlertsAction: 'Action',
  dashCustomAlertsDelete: 'Delete',
  // ── Dashboard 2FA ──
  dash2FATitle: 'Two-factor authentication',
  dash2FADesc: '2FA protects your account with an extra layer of security via SMS.',
  dash2FAEnable: 'Enable 2FA',
  dash2FADisable: 'Disable 2FA',
  dash2FAEnabled: 'Enabled — your account is protected.',
  dash2FADisabled: 'Disabled — recommended to enable.',
  // ── Dashboard API ──
  dashApiTitle: 'API access',
  dashApiKey: 'API key',
  dashApiDocs: 'Documentation',
  dashApiUsage: 'API usage this month',
  dashApiCalls: 'calls',
  dashApiGenerate: 'Generate new key',
  // ── Dashboard Feedback ──
  dashFeedbackTitle: 'Feedback & Reviews',
  dashFeedbackAvg: 'Average rating',
  dashFeedbackTotal: 'Total ratings',
  dashFeedbackNoData: 'No feedback yet.',
  // ── Dashboard Announcements ──
  dashAnnouncementsTitle: 'Announcements',
  dashAnnouncementsMarkRead: 'Mark as read',
  dashAnnouncementsNoData: 'No announcements.',
  // ── Settings Page ──
  settingsAccount: 'Account',
  settingsTitle: 'Settings',
  settingsEmail: 'Email address',
  settingsCurrentEmail: 'Current:',
  settingsNewEmail: 'New email address',
  settingsUpdateEmail: 'Update email',
  settingsUpdating: 'Updating...',
  settingsPassword: 'Password',
  settingsNewPassword: 'New password',
  settingsConfirmPassword: 'Confirm new password',
  settingsChangePassword: 'Change password',
  settingsPasswordMin: 'Password must be at least 8 characters.',
  settingsPasswordMismatch: 'Passwords do not match.',
  settings2FA: 'Two-factor authentication',
  settings2FADesc: '2FA protects your account with an extra layer of security.',
  settings2FAEnabled: 'Enabled — your account is protected.',
  settings2FADisabled: 'Disabled — recommended to enable.',
  settingsNotifications: 'Notification settings',
  settingsEmailNotif: 'Email notifications',
  settingsEmailNotifDesc: 'Receive important notifications by email',
  settingsSmsNotif: 'SMS notifications',
  settingsSmsNotifDesc: 'Receive notifications via SMS',
  settingsProductUpdates: 'Product updates',
  settingsProductUpdatesDesc: 'News about machines and services',
  settingsNewsletter: 'Newsletter',
  settingsNewsletterDesc: 'Monthly newsletter from SebNorge',
  settingsLanguage: 'Language',
  settingsDangerZone: 'Danger zone',
  settingsDeleteAccount: 'Delete account',
  settingsDeleteDesc: 'All data is permanently deleted and cannot be recovered.',
  settingsDeleteBtn: 'Delete account',
  settingsDeleteConfirmTitle: 'Delete account',
  settingsDeleteConfirmDesc: 'Are you sure? This action cannot be undone.',
  settingsDeleteConfirmType: 'Type DELETE to confirm.',
  settingsDeleteConfirmBtn: 'Confirm deletion',
  settingsDeleteConfirming: 'Deleting...',
  settingsCancel: 'Cancel',
  settingsEmailConfirmSent: 'Confirmation link sent to new email address.',
  settingsEmailError: 'Error updating email.',
  settingsPasswordUpdated: 'Password updated.',
  settingsPasswordError: 'Error updating password.',
  settingsDarkMode: 'Dark mode',
  settingsDarkModeDesc: 'Switch between dark and light theme',
  // ── Blog Page ──
  blogTitle: 'Blog & News',
  blogSubtitle: 'Latest from SebNorge — machines, updates and industry news.',
  blogReadMore: 'Read more',
  blogSubscribe: 'Subscribe to newsletter',
  blogSubscribePlaceholder: 'Your email address',
  blogSubscribeBtn: 'Subscribe',
  blogSubscribeSuccess: 'You are now subscribed!',
  blogCategory: 'Category',
  blogDate: 'Date',
  // ── Karriere Page ──
  karriereTitle: 'Careers at SebNorge',
  karriereSubtitle: 'Join our team and help shape the future of industry.',
  karriereApply: 'Apply now',
  karriereLocation: 'Location',
  karriereDepartment: 'Department',
  karriereApplyTitle: 'Apply for position',
  karriereNameLabel: 'Full name',
  karriereEmailLabel: 'Email address',
  karrierePhoneLabel: 'Phone number',
  karriereCvLabel: 'Upload CV',
  karriereMessageLabel: 'Message',
  karriereSubmitBtn: 'Submit application',
  karriereSuccess: 'Application submitted! We will be in touch soon.',
  karriereNoJobs: 'No open positions at the moment.',
  // ── Partnere Page ──
  partnereTitle: 'Our partners',
  partnereSubtitle: 'SebNorges technology and distribution partners across Norway and Europe.',
  partnereCountry: 'Country',
  partnereDescription: 'Description',
  // ── Status Page ──
  statusTitle: 'System status',
  statusSubtitle: 'Real-time status for all SebNorge services.',
  statusOperational: 'Operational',
  statusOutage: 'Outage',
  statusDegraded: 'Degraded',
  statusHistory: '90-day history',
  statusLastChecked: 'Last checked',
  statusAllOperational: 'All systems operational',
  // ── Onboarding ──
  onboardingTitle: 'Welcome to SebNorge',
  onboardingSubtitle: "Let's set up your account.",
  onboardingNext: 'Next',
  onboardingBack: 'Back',
  onboardingFinish: 'Finish',
  onboardingStep1Title: 'Choose your plan',
  onboardingStep2Title: 'About your company',
  onboardingStep3Title: 'Confirm and start',
  onboardingSelectPlan: 'Select plan',
  onboardingCompanyLabel: 'Company name',
  onboardingIndustryLabel: 'Industry',
  onboardingMachineCount: 'Number of machines',
  onboardingComplete: 'Account configured!',
  // ── Faktura Page ──
  fakturaTitle: 'Invoices',
  fakturaSubtitle: 'Overview of all your invoices.',
  fakturaColDate: 'Date',
  fakturaColAmount: 'Amount',
  fakturaColStatus: 'Status',
  fakturaColPlan: 'Plan',
  fakturaDownload: 'Download',
  fakturaNoData: 'No invoices found.',
  fakturaPaid: 'Paid',
  fakturaPending: 'Pending',
  fakturaOverdue: 'Overdue',
  // ── Common ──
  commonSave: 'Save',
  commonCancel: 'Cancel',
  commonDelete: 'Delete',
  commonEdit: 'Edit',
  commonClose: 'Close',
  commonDownload: 'Download',
  commonRetry: 'Try again',
  commonLoading: 'Loading...',
  commonNoData: 'No data available.',
  commonError: 'Something went wrong. Please try again.',
  commonSearch: 'Search',
  commonFilter: 'Filter',
  commonAll: 'All',
  commonSubmit: 'Submit',
  commonBack: 'Back',
  commonNext: 'Next',
  commonConfirm: 'Confirm',
  // ── Toast Messages ──
  toastLoginSuccess: 'Login successful — welcome back!',
  toastSignupSuccess: 'Account created! Check your phone for a welcome SMS.',
  toastLogoutSuccess: 'You are now logged out.',
  toastSaveSuccess: 'Changes saved.',
  toastDeleteSuccess: 'Deleted.',
  toastError: 'Something went wrong. Please try again.',
  toastContactSuccess: 'Message sent! We will be in touch soon.',
  toastNewsletterSuccess: 'You are now subscribed!',
  toastMaintenanceSuccess: 'Maintenance request submitted!',
  toastOrderSuccess: 'Order received! Invoice sent by email.',
  toastWarrantySuccess: 'Warranty claim submitted.',
  toastAlertSaved: 'Alert saved.',
  toastPasswordUpdated: 'Password updated.',
  toastEmailUpdated: 'Email updated.',
  // ── Browser Tab Titles ──
  tabHome: 'SebNorge — Industrial machines',
  tabMachines: 'Machines — SebNorge',
  tabPricing: 'Subscription — SebNorge',
  tabDashboard: 'Dashboard — SebNorge',
  tabSettings: 'Settings — SebNorge',
  tabFaktura: 'Invoice — SebNorge',
  tabBlog: 'Blog — SebNorge',
  tabKarriere: 'Careers — SebNorge',
  tabPartnere: 'Partners — SebNorge',
  tabStatus: 'System status — SebNorge',
  tabOnboarding: 'Get started — SebNorge',
  // ── New Visual Sections ──
  trustedByLabel: 'Trusted by companies across 38 countries',
  videoLabel: 'See us in action',
  videoTitle: 'See SebNorge in action',
  videoSubtitle: 'Watch how our machines perform in real industrial environments.',
  numbersLabel: 'Our numbers',
  testimonialsLabel: 'Customer testimonials',
  testimonialsTitle: 'What our customers say',
  mapLabel: 'Global reach',
  mapTitle: '38 countries. One standard of quality.',
  mapSubtitle: 'SebNorge delivers industrial precision to customers across 38 countries and 6 continents.',
  // ── Billing Screen ──
  billingTitle: 'Billing',
  billingCurrentPlan: 'Current plan',
  billingPlanStatus: 'Plan status',
  billingMonthly: 'Monthly billing',
  billingNextInvoice: 'Next invoice',
  billingDaysRemaining: 'days remaining',
  billingUpgrade: 'Upgrade',
  billingDowngrade: 'Downgrade',
  billingUsageTitle: 'Usage statistics',
  billingMachines: 'Machines available',
  billingApiCalls: 'API calls this month',
  billingTeamMembers: 'Active team members',
  billingStorage: 'Data storage used',
  billingInvoiceHistory: 'Invoice history',
  billingInvoiceNo: 'Invoice no.',
  billingDate: 'Date',
  billingPlan: 'Plan',
  billingAmount: 'Amount',
  billingStatus: 'Status',
  billingDownloadPdf: 'Download PDF',
  billingNoInvoices: 'No invoices yet.',
  billingRecipientTitle: 'Invoice recipient',
  billingRecipientCompany: 'Company name',
  billingRecipientOrg: 'Org. no.',
  billingRecipientAddress: 'Address',
  billingRecipientEmail: 'Email',
  billingRecipientPhone: 'Phone',
  billingAddRecipient: '+ Add invoice recipient',
  billingEditRecipient: 'Edit',
  billingNoRecipient: 'No invoice recipient registered.',
  billingPlanOptions: 'Plan options',
  billingChoosePlan: 'Choose plan',
  billingCurrentBadge: 'Current',
  billingStatusActive: 'Active',
  billingStatusInactive: 'Inactive',
  billingStatusTrial: 'Trial',
  billingStatusPaid: 'Paid',
  billingStatusPending: 'Pending',
  billingStatusOverdue: 'Overdue',
  billingDowngradeTitle: 'Downgrade plan',
  billingDowngradeConfirm: 'Confirm downgrade',
  billingDowngradeCancel: 'Cancel',
  billingDowngradeWarning: 'You will lose access to premium features on the next billing date.',
  tabFakturering: 'Billing — SebNorge',
}

const TRANSLATIONS: Record<Lang, Translations> = { no: NO, en: EN };

interface LanguageContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: 'no',
  setLang: () => {},
  t: NO,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>('no');

  useEffect(() => {
    const saved = localStorage.getItem('sebnorge_lang') as Lang | null;
    if (saved === 'no' || saved === 'en') {
      setLangState(saved);
    }
  }, []);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    if (typeof window !== 'undefined') {
      localStorage.setItem('sebnorge_lang', l);
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: TRANSLATIONS[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export { TRANSLATIONS };
